import { type FormEvent, useCallback, useEffect, useId, useRef, useState } from 'react'
import {
  confirmMfaEnrollment,
  disableMfaSelf,
  getMfaStatus,
  mfaErrorMessage,
  startMfaEnrollment,
  type MfaEnrollmentStart,
  type MfaStatus,
} from '../api/mfa'
import { QrCode } from '../components/QrCode'
import { MfaCodeInput } from '../components/MfaCodeInput'
import { Page, PageHeader, Button, Callout, Card, CardHeader, CardContent, LoadingState, TextField, CardGrid } from '../components/ui'

/**
 * F-041 / REQ-032-002. Il proprio account — oggi solo il secondo fattore (TOTP), raggiungibile dal
 * profilo. Chiede lo stato a `GET /api/auth/mfa/status` (`AuthController.MfaStatus`) all'apertura e
 * mostra solo ciò che ha senso: se il secondo fattore è già attivo, solo la scheda di
 * disattivazione (con da quando è attivo e quanti codici di recupero restano); se non lo è, solo la
 * scheda di attivazione. Se la richiesta di stato fallisce, la pagina non resta vuota né bloccata:
 * mostra un avviso e ricade sul comportamento di F-040 (entrambe le azioni disponibili).
 */
export function AccountSecurityPage() {
  const [status, setStatus] = useState<MfaStatus | null>(null)
  const [statusLoading, setStatusLoading] = useState(true)
  const [statusError, setStatusError] = useState<string | null>(null)

  const refreshStatus = useCallback(async () => {
    setStatusLoading(true)
    setStatusError(null)
    try {
      const result = await getMfaStatus()
      setStatus(result)
    } catch (err) {
      // Non deve bloccare la pagina: `status` resta quello che era (probabilmente `null` al primo
      // caricamento), e sotto rendiamo comunque qualcosa di utilizzabile invece di una pagina vuota.
      setStatusError(mfaErrorMessage(err))
    } finally {
      setStatusLoading(false)
    }
  }, [])

  useEffect(() => {
    void refreshStatus()
  }, [refreshStatus])

  return (
    <Page>
      <PageHeader title="Sicurezza account" icon="shield_person" />

      {statusLoading && <LoadingState label="Verifica dello stato del secondo fattore…" />}

      {!statusLoading && statusError && (
        <Callout tone="warning">
          Non è stato possibile verificare lo stato del secondo fattore ({statusError}). Puoi
          comunque attivarlo o disattivarlo qui sotto: in entrambi i casi ti verrà chiesta la
          prova che serve.
        </Callout>
      )}

      {!statusLoading && status?.roleMandateActive && !status.active && (
        <Callout tone="error" icon="gpp_maybe">
          <strong>Il tuo ruolo richiede il secondo fattore di autenticazione.</strong> Attivalo
          qui sotto il prima possibile.
        </Callout>
      )}

      {!statusLoading && status?.active && (
        <Callout tone="success" icon="verified_user" role="status">
          Secondo fattore attivo
          {status.activeSince && ` dal ${new Date(status.activeSince).toLocaleDateString('it-IT')}`}.
          {' '}
          {status.remainingRecoveryCodes <= 2 ? (
            <strong>
              Ti rimangono solo {status.remainingRecoveryCodes} {status.remainingRecoveryCodes === 1 ? 'codice di recupero' : 'codici di recupero'}.
              {' '}Se li esaurisci e perdi l'app di autenticazione, non potrai più accedere: disattiva e riattiva il secondo fattore per generarne di nuovi.
            </strong>
          ) : (
            <>Codici di recupero non ancora usati: {status.remainingRecoveryCodes}.</>
          )}
        </Callout>
      )}

      {!statusLoading && (
        <CardGrid min={360}>
          {/* Stato noto: mostra solo la scheda che ha senso. Stato sconosciuto (richiesta di
              stato fallita, `status` ancora `null`): fallback di F-040, entrambe le azioni. */}
          {status ? (
            status.active ? (
              <DisableCard onDisabled={() => { void refreshStatus() }} />
            ) : (
              <EnrollmentCard onActivated={() => { void refreshStatus() }} />
            )
          ) : (
            <>
              <EnrollmentCard onActivated={() => { void refreshStatus() }} />
              <DisableCard onDisabled={() => { void refreshStatus() }} />
            </>
          )}
        </CardGrid>
      )}
    </Page>
  )
}

type EnrollStep = 'start' | 'confirm' | 'recovery-codes'

export function EnrollmentCard({ onActivated }: { onActivated: () => void }) {
  const [step, setStep] = useState<EnrollStep>('start')
  const [pending, setPending] = useState<MfaEnrollmentStart | null>(null)
  const [code, setCode] = useState('')
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([])
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const codeInputId = useId()
  const codeInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

  async function handleStart() {
    setError(null)
    setLoading(true)
    try {
      const result = await startMfaEnrollment()
      setPending(result)
      setCode('')
      setStep('confirm')
    } catch (err) {
      setError(mfaErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  // Accessibility: sposta il focus sul campo codice appena la schermata di conferma appare,
  // così chi usa tastiera/screen reader non deve cercarlo.
  useEffect(() => {
    if (step === 'confirm') codeInputRef.current?.focus()
  }, [step])

  async function handleConfirm(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const result = await confirmMfaEnrollment(code)
      setRecoveryCodes(result.recoveryCodes)
      setStep('recovery-codes')
      // Il segreto pendente e il codice inserito hanno esaurito il loro scopo: non devono
      // restare in stato oltre il necessario.
      setPending(null)
      setCode('')
    } catch (err) {
      setError(mfaErrorMessage(err))
      setCode('')
      codeInputRef.current?.focus()
    } finally {
      setLoading(false)
    }
  }

  function handleReset() {
    setStep('start')
    setPending(null)
    setCode('')
    setRecoveryCodes([])
    setCopied(false)
    setError(null)
  }

  // Distinto da handleReset: qui l'attivazione è già avvenuta con successo (siamo nello step
  // 'recovery-codes'), quindi oltre a ripulire lo stato locale bisogna dire al genitore di
  // rileggere lo stato dal backend, così la pagina passa a mostrare solo la scheda di
  // disattivazione invece di lasciare questa scheda pronta a un nuovo "Genera codice segreto"
  // che il backend rifiuterebbe comunque (il secondo fattore è già attivo).
  function handleFinish() {
    handleReset()
    onActivated()
  }

  async function handleCopyRecoveryCodes() {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join('\n'))
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    } catch {
      // Clipboard API non disponibile/negata: i codici restano comunque visibili e selezionabili
      // a mano, quindi non è un errore bloccante — nessun alert, solo niente feedback "copiato".
    }
  }

  return (
    <Card variant="outlined">
      <CardHeader title="Attiva il secondo fattore (app di autenticazione)" icon="qr_code_2" />
      <CardContent>
        <div className="d-flex flex-column gap-3">
          {error && <Callout tone="error" id={`${codeInputId}-error`}>{error}</Callout>}

          {step === 'start' && (
            <>
              <p className="text-muted md-typescale-body-small mb-0">
                Genera un nuovo codice segreto da collegare a un'app come Google Authenticator, Microsoft
                Authenticator o Authy.
              </p>
              <div>
                <Button variant="filled" disabled={loading} onClick={() => { void handleStart() }}>
                  {loading ? 'Generazione…' : 'Genera codice segreto'}
                </Button>
              </div>
            </>
          )}

          {step === 'confirm' && pending && (
            <>
              <p className="text-muted md-typescale-body-small mb-0">
                Inquadra il QR con l'app di autenticazione, oppure inserisci il codice segreto a mano se
                non puoi inquadrarlo. Poi conferma con il codice a 6 cifre che l'app genera.
              </p>
              <div className="d-flex flex-column align-items-center gap-3">
                <QrCode value={pending.otpauthUri} size={200} />
                <div className="text-center">
                  <div className="text-muted md-typescale-body-small mb-1">Codice segreto (se non puoi inquadrare il QR)</div>
                  <code className="md-typescale-body-large user-select-all md-u-break-word">
                    {pending.secret}
                  </code>
                </div>
              </div>

              <form onSubmit={(e) => { void handleConfirm(e) }} className="d-flex flex-column gap-3">
                <TextField
                  label="Codice a 6 cifre"
                  id={codeInputId}
                  ref={codeInputRef}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  aria-describedby={error ? `${codeInputId}-error` : undefined}
                  error={error ? 'Controlla il codice inserito' : undefined}
                />
                <div className="d-flex gap-2">
                  <Button type="submit" variant="filled" disabled={loading || code.length !== 6}>
                    {loading ? 'Verifica…' : 'Conferma e attiva'}
                  </Button>
                  <Button variant="text" onClick={handleReset} disabled={loading}>
                    Annulla
                  </Button>
                </div>
              </form>
            </>
          )}

          {step === 'recovery-codes' && (
            <>
              <Callout tone="success" role="alert">Secondo fattore attivato con successo.</Callout>

              <Callout tone="warning" role="alert">
                <strong>Salva questi codici di recupero adesso.</strong> Non verranno mostrati mai più:
                se perdi l'app di autenticazione, sono l'unico modo per rientrare nel tuo account.
                Conservali in un posto sicuro (un password manager, per esempio).
              </Callout>

              <div
                className="p-3 md-u-copy-grid"
              >
                {recoveryCodes.map(rc => (
                  <div key={rc}>{rc}</div>
                ))}
              </div>

              <div className="d-flex align-items-center gap-2">
                <Button variant="outlined" size="sm" icon="content_copy" onClick={() => { void handleCopyRecoveryCodes() }}>
                  Copia negli appunti
                </Button>
                {copied && <span className="text-success md-typescale-body-small">Copiato.</span>}
              </div>

              <div>
                <Button variant="filled" onClick={handleFinish}>
                  Ho salvato i codici, fatto
                </Button>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function DisableCard({ onDisabled }: { onDisabled: () => void }) {
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const codeInputId = useId()

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await disableMfaSelf(code)
      setDone(true)
      setCode('')
    } catch (err) {
      setError(mfaErrorMessage(err))
      setCode('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card variant="outlined">
      <CardHeader title="Disattiva il secondo fattore" icon="remove_moderator" />
      <CardContent>
        {done ? (
          <div className="d-flex flex-column gap-3">
            <Callout tone="success" role="alert">
              Secondo fattore disattivato. Da ora il login richiede solo la password.
            </Callout>
            {/* Lascia il messaggio visibile finché non è l'utente stesso a chiudere — solo allora
                il genitore rilegge lo stato e passa a mostrare la scheda di attivazione. */}
            <div>
              <Button variant="outlined" size="sm" onClick={onDisabled}>
                Fatto
              </Button>
            </div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            <p className="text-muted md-typescale-body-small mb-0">
              Per disattivare il secondo fattore devi provare di poterlo ancora usare: inserisci un
              codice a 6 cifre dell'app di autenticazione oppure uno dei tuoi codici di recupero.
            </p>
            {error && <Callout tone="error" role="alert">{error}</Callout>}
            <form onSubmit={(e) => { void handleSubmit(e) }} className="d-flex flex-column gap-3">
              <MfaCodeInput
                id={codeInputId}
                value={code}
                onChange={setCode}
                disabled={loading}
                ariaInvalid={!!error}
              />
              <div>
                <Button type="submit" variant="tonal" disabled={loading || !code}>
                  {loading ? 'Verifica…' : 'Disattiva secondo fattore'}
                </Button>
              </div>
            </form>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
